<template>
  <div class="text--primary">
    <v-row dense>
      <v-col cols="12" sm="6" class="text-truncate">
        <template v-if="itemAprovacao.produto && itemAprovacao.produto.codigo">
          <span class="font-weight-bold">Código interno:</span>
          {{ itemAprovacao.produto.codigo }}
          <v-icon small>mdi-pound</v-icon>
        </template>
        <px-situacao-aprovacao class="ml-3" :situacao="itemAprovacao.situacao"/>
        <v-chip label small v-if="isItemCancelado"
                class="ml-2"
                color="error">
          Cancelado
        </v-chip>
      </v-col>
      <v-col cols="12" sm="6" class="text-right">
        <px-aprovacao-compra-produto-controls show-as-buttons
                                              :aprovacao-id="aprovacaoId"
                                              :item-aprovacao="itemAprovacao"
                                              @update="onUpdate"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="body-1 font-weight-bold">
        <px-produto-descricao :show-codigo="false"
                              :especificacao="itemAprovacao.especificacaoProduto"
                              :produto="itemAprovacao.produto"/>
        <px-historico-preco-produto v-if="hasProduto"
                                    small
                                    :produto="itemAprovacao.produto"
                                    :unidade-medida="itemAprovacao.unidadeMedida"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        {{ itemAprovacao.quantidade }}
        {{ itemAprovacao.unidadeMedida.descricao }}
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" v-if="hasProduto">
        <span class="caption font-weight-bold">Especificação</span>
        <div>
          {{ itemAprovacao.especificacaoProduto | naoInformado}}
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Preço unitário</span>
        <div>
          {{
            itemAprovacao.valorMoedaConvertido.precoUnitario
              | currency('R$', itemAprovacao.produto.casasDecimais)
          }}
          <px-moeda-chip :moeda="moeda" v-if="isMoedaEstrangeira"/>
          <div class="caption" v-if="isMoedaEstrangeira">
            {{
              itemAprovacao.precoUnitario
                | currency(simboloMoeda,
                itemAprovacao.produto.casasDecimais,
                configuracaoFormatacaoMoeda)
            }}
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">ICMS</span>
        <px-imposto
          :imposto="itemAprovacao.impostos"
          show-valor-convertido
          tipo="ICMS"
          :show-aplicacao="false"
          :scale="itemAprovacao.produto.casasDecimais"/>
        <div v-if="isMoedaEstrangeira">
          <px-imposto
            class="caption"
            :imposto="itemAprovacao.impostos"
            :simbolo-moeda="simboloMoeda"
            :configuracao-moeda="configuracaoFormatacaoMoeda"
            tipo="ICMS"
            :show-aplicacao="false"
            :scale="itemAprovacao.produto.casasDecimais"/>
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">IPI</span>
        <px-imposto
          show-valor-convertido
          :imposto="itemAprovacao.impostos"
          tipo="IPI"
          :show-aplicacao="false"
          :scale="itemAprovacao.produto.casasDecimais"/>
        <div v-if="isMoedaEstrangeira">
          <px-imposto
            class="caption"
            :imposto="itemAprovacao.impostos"
            :simbolo-moeda="simboloMoeda"
            :configuracao-moeda="configuracaoFormatacaoMoeda"
            tipo="IPI"
            :show-aplicacao="false"
            :scale="itemAprovacao.produto.casasDecimais"/>
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Total</span>
        <div>
          {{
            itemAprovacao.valorMoedaConvertido.total
              | currency('R$', itemAprovacao.produto.casasDecimais)
          }}
          <div class="caption" v-if="isMoedaEstrangeira">
            {{
              itemAprovacao.total
                | currency(simboloMoeda,
                itemAprovacao.produto.casasDecimais,
                configuracaoFormatacaoMoeda)
            }}
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" sm="3" v-if="showCentroCusto">
        <span class="caption font-weight-bold">Centro de custo</span>
        <div>
          {{ centroCusto | naoInformado }}
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Projeto</span>
        <div>
          {{ projeto | naoInformado }}
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Data de entrega solicitada</span>
        <div>
          {{ itemAprovacao.dataPrevisaoEntrega | date | naoInformado }}
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Finalidade da compra</span>
        <div v-if="itemAprovacao.finalidadeCompra">
          <px-finalidade-compra-value :finalidade-compra="itemAprovacao.finalidadeCompra"/>
        </div>
        <div v-else>
          Não informado
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Observações internas</span>
        <div>
          <px-show-large-text :large-text="itemAprovacao.observacaoInterna"/>
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Observações externas</span>
        <div>
          <px-show-large-text :large-text="itemAprovacao.observacaoExterna"/>
        </div>
      </v-col>
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Código de referência</span>
        <div>
          {{ codigoReferencia(itemAprovacao.produto) | naoInformado }}
        </div>
      </v-col>
      <v-col cols="12" sm="3" v-if="itemAprovacao.arquivos && itemAprovacao.arquivos.length">
        <span class="caption font-weight-bold">Anexos</span>
        <px-anexo-chip :files="itemAprovacao.arquivos"
                       download-base-path="files/download-comprador"/>
      </v-col>
    </v-row>
    <v-row v-if="hasProduto && itemAprovacao.produto.precoTarget">
      <v-col cols="12" sm="3">
        <span class="caption font-weight-bold">Preço target</span>
        <div>
          {{ itemAprovacao.produto.precoTarget | currency }}
        </div>
      </v-col>
    </v-row>
    <v-row v-if="itemAprovacao.motivoRecusa">
      <v-col cols="12">
        <span class="caption font-weight-bold">
          <v-icon small color="warning" class="pb-1">mdi-alert</v-icon> Motivo da recusa
        </span>
        <div>
          {{ itemAprovacao.motivoRecusa }}
        </div>
      </v-col>
    </v-row>
    <px-campos-integracao
      v-if="cadastrosAdicionaisItem && erpCamposIntegracao"
      type="compra-produtos-view"
      :form-data="cadastrosAdicionaisItem"/>
  </div>
</template>

<script>
import { descWithCodigoInterno } from '@/utils/object-utils';
import { cadastrosAdicionaisToMap } from '@/utils/campos-integracao-utils';
import { mapGetters } from 'vuex';
import moedaMixin from '@/mixins/moeda-mixin';
import AprovacaoCompraProdutoControls
  from '@/modules/aprovacao-compra/components/AprovacaoCompraProdutoControls.vue';
import { FornecedorProdutoUtils } from 'px-components';
import { SituacaoItemCompra } from 'px-business-components';
import EntidadeCamposCompraService from '../../erp-campos/entidade-campos-compra-service';
import { isFieldHiddenEntityItens } from '../../erp-campos/erp-campos-utils';

export default {
  mixins: [moedaMixin],
  props: {
    aprovacaoId: {
      type: [String, Number],
    },
    fornecedorId: {
      type: [String, Number],
    },
    itemAprovacao: {
      type: Object,
    },
  },
  data() {
    return {
      camposHidden: null,
    };
  },
  computed: {
    ...mapGetters('entidade', ['erpCamposIntegracao']),
    moeda() {
      return this.itemAprovacao.aprovacaoCompra.moeda;
    },
    hasProduto() {
      return !!this.itemAprovacao.produto;
    },
    centroCusto() {
      return this.itemAprovacao.centroCusto
        ? descWithCodigoInterno(this.itemAprovacao.centroCusto) : null;
    },
    projeto() {
      return this.itemAprovacao.projeto
        ? descWithCodigoInterno(this.itemAprovacao.projeto) : null;
    },
    cadastrosAdicionaisItem() {
      if (!this.itemAprovacao.cadastrosAdicionais
        || !this.itemAprovacao.cadastrosAdicionais.length) {
        return null;
      }
      return cadastrosAdicionaisToMap(this.itemAprovacao.cadastrosAdicionais);
    },
    isItemCancelado() {
      return this.itemAprovacao.situacao === SituacaoItemCompra.keys.CANCELADO.key;
    },
    codigoReferencia() {
      return (produto) => {
        if (!produto) {
          return null;
        }
        const { fornecedoresProduto } = produto;
        return FornecedorProdutoUtils
          .getCodigoReferenciaFornecedor(fornecedoresProduto, this.fornecedorId);
      };
    },
    showCentroCusto() {
      return !isFieldHiddenEntityItens('centroCusto', this.camposHidden);
    },
  },
  methods: {
    onUpdate(aprovacaoAtualizada) {
      this.$emit('update', aprovacaoAtualizada);
    },
    getCamposHidden() {
      EntidadeCamposCompraService.getCamposHidden(this.erpCamposIntegracao)
        .then((data) => {
          this.camposHidden = data;
        });
    },
  },
  created() {
    this.getCamposHidden();
  },
  components: {
    pxAprovacaoCompraProdutoControls: AprovacaoCompraProdutoControls,
  },
};
</script>
